import {
  DealType,
  IConfiguration,
  IConfigurationInput,
  Maybe
} from 'src/types/graphql'

// ---------------------------------------------------------------------------
// Deal-type helpers — CANONICAL ENTRY POINTS
//
// **Stop here before writing another `dealType ?? Monthly` or
// `price > 30000` check anywhere in the codebase.** The four exports below
// are the only callers you need. Every other check is drift waiting to
// happen.
//
// | Helper                          | When to use                                          |
// | ------------------------------- | ---------------------------------------------------- |
// | `getEffectiveDealType(config)`  | THE answer to "what funnel is this listing in?".     |
// |                                 | Works with a full IConfiguration OR a thin           |
// |                                 | `{dealType, price}` shape. Resolution order:         |
// |                                 |   1. Explicit `dealType` wins (Traditional/Broker/   |
// |                                 |      Monthly — Monthly stays Monthly even on bodega/ |
// |                                 |      nave pillars: rare but allowed.)                |
// |                                 |   2. `price > $30k` → Broker.                        |
// |                                 |   3. `config.pillarPage` in B2B_PILLAR_SLUGS         |
// |                                 |      (`renta-bodegas-comerciales` /                  |
// |                                 |      `renta-naves-industriales`) → Broker.           |
// |                                 |   4. Fallback → Monthly.                             |
// |                                 | Use OUTSIDE the listing-detail tree (analytics,      |
// |                                 | server calls, edit flows, dashboards).               |
// | `isBrokerVariant / isMonthly… / | Boolean wrapper around `getEffectiveDealType`.       |
// |  isTraditionalVariant`          | Use in conditionals: `if (isBrokerVariant(config))`. |
// | `getEffectiveDealTypeFromPrice` | EDIT MODE ONLY. Ignores stored dealType and          |
// |                                 | recomputes from the user's current input price.      |
// |                                 | Used by EditConfig / AdminEdit / AddPostFlow.        |
// | `useListingVariant()` /         | INSIDE the listing-detail tree. Same dealType        |
// |  `useVariantSlice('meta')`      | as `getEffectiveDealType` but memoized + carries     |
// |                                 | admin `?variant=` querystring overrides.             |
//
// **Do not import or call `isB2BPillarListing` directly.** It's exported
// only because `resolveListingVariant` layers it with a currency-aware
// price hint that the pure helper can't see (`useGetPrice` is a React
// hook). Every other call site should use `getEffectiveDealType` —
// it already applies the pillar rule when given a full config.
//
// **B2B pillar detection is now string-based, not sqm-based.** The
// frontend reads `config.pillarPage` (resolved server-side by
// `resolvePillarCategory`) and checks membership in `B2B_PILLAR_SLUGS`.
// No more client-side sqm math. Backend is the source of truth — if the
// backend re-classifies a listing's pillar, the frontend automatically
// agrees on the next page render.
//
// **Server mirror:** `getEffectiveDealType` mirrors the server's
// `resolveDealType` in `spotme-server/src/api/location/prices.ts:32`.
// Any change to one must apply to the other in the same PR. The server
// applies the warehouse/industrial promotion separately in
// `resolveRenterCustomerType` (HubSpot customer-type derivation).
//
// **Known backend cleanup follow-up:** the sqm thresholds in
// `resolvePillarCategory` (> 40 / > 3000) don't match the SIZES filter
// (Commercial = 50-500 / Industrial = 500-3000 / Nave = 3000+) on the
// search/pillar pages. Centralize that constant on the backend and have
// both consumers read from it. Not blocking the frontend cutover.
//
// **Listing variant ↔ analytics agreement (CRITICAL):** any code path
// that fires a pixel event from a listing context MUST resolve dealType
// via this helper so the segmented event name (e.g.
// `contact_wa_storage_now` vs `contact_wa_storage_broker`) matches the
// variant the listing page rendered. Drift caused a $15k bodega to fire
// `contact_wa_storage_now` → polluted `storage_secondary` → PMax began
// optimizing for broker traffic. See
// [docs/marketing/analytics-reference.md §1.10](docs/marketing/analytics-reference.md)
// and [TIER_ROLLUP_MAP in spotmeEvents.ts](src/utils/pixels/spotmeEvents.ts).
// ---------------------------------------------------------------------------

/**
 * Monthly price (MXN) above which an un-tagged listing is treated as Broker.
 * Mirrors `PRICE_THRESHOLD` in `spotme-server/src/api/location/prices.ts:26`.
 */
export const PRICE_THRESHOLD = 30000

/**
 * Pillar slugs that map to broker (off-platform B2B) territory. Mirrors
 * backend `resolvePillarCategory` → `PILLAR_CATEGORY_URLS` for the
 * Warehouse + Industrial categories. Keep this in sync if the backend
 * ever renames a pillar slug.
 */
export const B2B_PILLAR_SLUGS = new Set<string>([
  'renta-bodegas-comerciales',
  'renta-naves-industriales'
])

/**
 * Minimum shape needed to derive the effective deal type. A full
 * `IConfiguration` enables the pillar promotion rule (`pillarPage`);
 * a thin `{dealType, price}` shape falls back to the price-only verdict.
 * `resolvedDealType` is the server-computed value (Configuration field
 * resolver) and short-circuits the client-side fallback when present —
 * only `IConfiguration` carries it; the lean `Pick` and input variants
 * do not.
 */
type ConfigLike =
  | Maybe<
      | IConfiguration
      | IConfigurationInput
      | Pick<IConfiguration, 'dealType' | 'price'>
    >
  | undefined

type ConfigInput = ConfigLike

/**
 * B2B pillar listing check, driven by the backend-resolved `pillarPage`
 * field on the configuration (computed by `resolvePillarCategory` on the
 * server, exposed via the GraphQL resolver). Returns true when the
 * listing is routed to `renta-bodegas-comerciales` or
 * `renta-naves-industriales`.
 *
 * Exported only because `resolveListingVariant` layers it with a
 * currency-aware price hint (`useGetPrice`, a React hook). **Every other
 * caller should use `getEffectiveDealType` instead** — it applies this
 * rule automatically when the config carries `pillarPage`.
 */
export const isB2BPillarListing = (
  config: Maybe<{ pillarPage?: Maybe<string> }> | undefined
): boolean => !!config?.pillarPage && B2B_PILLAR_SLUGS.has(config.pillarPage)

/**
 * Canonical deal-type resolver. Returns one of the three `DealType`
 * values — never null/undefined. Switch/compare without nullish guards.
 *
 * **Reading B — explicit `dealType` always wins.** A host who tags a
 * bodega comercial as `Monthly` keeps Monthly funnel events, even though
 * it sits on a B2B pillar. Rare but allowed. Same for explicit Broker
 * on a small mini-bodega: stays Broker.
 *
 * **Resolution order:**
 *   0. `config.resolvedDealType` — server-resolved value (Configuration
 *      field resolver) wins when present. The server has already applied
 *      the full fallback chain (including pillar promotion) and exposes
 *      the result. The client-side fallback below only runs for thin
 *      shapes that don't carry this field — edit flows (`IConfigurationInput`),
 *      `Pick<...>` configs, and pre-deploy clients reading from
 *      not-yet-regenerated queries.
 *   1. `config.dealType` set → return it as-is (any of the three).
 *   2. `price > PRICE_THRESHOLD` → `Broker`.
 *   3. `pillarPage` in `B2B_PILLAR_SLUGS` → `Broker`.
 *   4. Otherwise → `Monthly`.
 *
 * Rules 2 + 3 only fire when `dealType` is unset (~99% of listings,
 * since the field was added post-launch and never backfilled). They
 * infer broker territory from the cheapest available signal.
 *
 * The server's `resolveDealType` in `spotme-server/src/api/location/prices.ts`
 * mirrors rules 1–4 exactly — both implementations MUST stay in sync.
 */
export const getEffectiveDealType = (config: ConfigInput): DealType => {
  // Server-resolved value short-circuits the client-side chain when the
  // field is present. Reads from full `IConfiguration` responses; absent
  // on thin shapes (edit-mode `IConfigurationInput`, `Pick<>` variants).
  const resolved = (config as { resolvedDealType?: Maybe<DealType> })
    ?.resolvedDealType
  if (resolved === DealType.Traditional) return DealType.Traditional
  if (resolved === DealType.Broker) return DealType.Broker
  if (resolved === DealType.Monthly) return DealType.Monthly

  if (config?.dealType === DealType.Traditional) return DealType.Traditional
  if (config?.dealType === DealType.Broker) return DealType.Broker
  if (config?.dealType === DealType.Monthly) return DealType.Monthly
  if ((config?.price ?? 0) > PRICE_THRESHOLD) return DealType.Broker
  if (isB2BPillarListing(config as Parameters<typeof isB2BPillarListing>[0])) {
    return DealType.Broker
  }
  return DealType.Monthly
}

/**
 * Edit-mode variant: ignore the stored `dealType` and recompute from the
 * user's current input price. Used by EditConfig / EditListing /
 * AdminEdit / AddPostFlow where the host is actively editing the price
 * and the stored dealType is stale until save.
 *
 * Does NOT apply the pillar rule — the host's edits to size happen in
 * a separate step, so a size-vs-price disagreement during edit isn't a
 * bug to defend against here.
 */
export const getEffectiveDealTypeFromPrice = (
  price: Maybe<number> | undefined
): DealType =>
  (price ?? 0) > PRICE_THRESHOLD ? DealType.Broker : DealType.Monthly

/** Convenience: `getEffectiveDealType(config) === DealType.Broker`. */
export const isBrokerVariant = (config: ConfigInput): boolean =>
  getEffectiveDealType(config) === DealType.Broker

/** Convenience: `getEffectiveDealType(config) === DealType.Traditional`. */
export const isTraditionalVariant = (config: ConfigInput): boolean =>
  getEffectiveDealType(config) === DealType.Traditional

/** Convenience: `getEffectiveDealType(config) === DealType.Monthly`. */
export const isMonthlyVariant = (config: ConfigInput): boolean =>
  getEffectiveDealType(config) === DealType.Monthly
